'use client';

import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import type { AnchorPropsInterface } from './types/anchor.types';

/**
 * A headless anchor that supports polymorphic composition and accessible disabled behavior.
 */
export const Anchor = forwardRef<HTMLAnchorElement, AnchorPropsInterface>(
  (props: AnchorPropsInterface, forwardedRef) => {
    const { asChild = false, className, disabled = false, ...rest } = props;
    const Component = asChild ? Slot : 'a';
    const resolvedClassName = typeof className === 'function' ? className({ disabled }) : className;

    // Anchors do not support the native disabled attribute. Keep href available for context-menu
    // copying while removing the element from the tab order and blocking activation.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <Component
        data-c11n-component="Anchor"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
        ref={forwardedRef}
        className={resolvedClassName}
        data-disabled={disabled ? '' : undefined}
        {...(disabled ? disabledInteractionsProps : {})}
      />
    );
  },
);

Anchor.displayName = 'Anchor';
